/**
 * File: Utils.js
 * Project: Noname-Base-Frontend
 * File Created: Friday, 17th January 2025 11:02:20 am
 * Author: Jorge Sierra Blázquez (jorge.sierra@aerin.es)
 * Version: 1.0.0
 * -----
 * Last Modified: Friday, 7th March 2025 5:56:19 pm
 * Modified By: Alexandru Ionut Dascalu
 * -----
 * Copyright (c) 2023 - 2025 Aerin Sistemas S. L. copying, distribution or modification not authorised in writing is prohibited.
 */

import { NotificationManager } from 'components/common/react-notifications';
import {
  defaultDirection,
  defaultLocale,
  defaultColor,
  localeOptions,
  themeColorStorageKey,
  themeRadiusStorageKey,
  adminMenus,
} from 'constants/defaultValues';

import axios from 'axios';

export const mapOrder = (array, order, key) => {
  // eslint-disable-next-line func-names
  array.sort(function (a, b) {
    const A = a[key];
    const B = b[key];
    if (order.indexOf(`${A}`) > order.indexOf(`${B}`)) {
      return 1;
    }
    return -1;
  });
  return array;
};

export const getDateWithFormat = () => {
  const today = new Date();
  let dd = today.getDate();
  let mm = today.getMonth() + 1; // January is 0!

  const yyyy = today.getFullYear();
  if (dd < 10) {
    dd = `0${dd}`;
  }
  if (mm < 10) {
    mm = `0${mm}`;
  }
  return `${dd}.${mm}.${yyyy}`;
};

export const getCurrentTime = () => {
  const now = new Date();
  return `${now.getHours()}:${now.getMinutes()}`;
};

export const getDirection = () => {
  let direction = defaultDirection;

  try {
    if (localStorage.getItem('direction')) {
      const localValue = localStorage.getItem('direction');
      if (localValue === 'rtl' || localValue === 'ltr') {
        direction = localValue;
      }
    }
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : getDirection -> error', error);
    direction = defaultDirection;
  }
  return {
    direction,
    isRtl: direction === 'rtl',
  };
};
export const setDirection = (localValue) => {
  let direction = 'ltr';
  if (localValue === 'rtl' || localValue === 'ltr') {
    direction = localValue;
  }
  try {
    localStorage.setItem('direction', direction);
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : setDirection -> error', error);
  }
};

export const setAxiosConfig = () => {
  const { REACT_APP_PROTOCOL, REACT_APP_BACK_IP, REACT_APP_BACK_PORT } =
    process.env;
  if (REACT_APP_BACK_PORT === '') {
    axios.defaults.baseURL = `${REACT_APP_PROTOCOL}://${REACT_APP_BACK_IP}`;
  } else {
    axios.defaults.baseURL = `${REACT_APP_PROTOCOL}://${REACT_APP_BACK_IP}:${REACT_APP_BACK_PORT}`;
  }
  const token = localStorage.getItem('token');
  if (token) {
    axios.defaults.headers.common.Authorization = `Token ${token}`;
    axios.defaults.headers.post['Content-Type'] = 'application/json';
  }
};

export const getCurrentColor = () => {
  let currentColor = defaultColor;
  try {
    if (localStorage.getItem(themeColorStorageKey)) {
      currentColor = localStorage.getItem(themeColorStorageKey);
    }
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : getCurrentColor -> error', error);
    currentColor = defaultColor;
  }
  return currentColor;
};

export const setCurrentColor = (color) => {
  try {
    localStorage.setItem(themeColorStorageKey, color);
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : setCurrentColor -> error', error);
  }
};

export const getCurrentRadius = () => {
  let currentRadius = 'rounded';
  try {
    if (localStorage.getItem(themeRadiusStorageKey)) {
      currentRadius = localStorage.getItem(themeRadiusStorageKey);
    }
  } catch (error) {
    console.log(
      '>>>>: src/helpers/Utils.js : getCurrentRadius -> error',
      error
    );
    currentRadius = 'rounded';
  }
  return currentRadius;
};
export const setCurrentRadius = (radius) => {
  try {
    localStorage.setItem(themeRadiusStorageKey, radius);
  } catch (error) {
    console.log(
      '>>>>: src/helpers/Utils.js : setCurrentRadius -> error',
      error
    );
  }
};

export const getCurrentLanguage = () => {
  let language = defaultLocale;
  try {
    language =
      localStorage.getItem('currentLanguage') &&
      localeOptions.filter(
        (x) => x.id === localStorage.getItem('currentLanguage')
      ).length > 0
        ? localStorage.getItem('currentLanguage')
        : defaultLocale;
  } catch (error) {
    console.log(
      '>>>>: src/helpers/Utils.js : getCurrentLanguage -> error',
      error
    );
    language = defaultLocale;
  }
  return language;
};
export const setCurrentLanguage = (locale) => {
  try {
    localStorage.setItem('currentLanguage', locale);
  } catch (error) {
    console.log(
      '>>>>: src/helpers/Utils.js : setCurrentLanguage -> error',
      error
    );
  }
};

export const getCurrentUser = () => {
  let user = null;
  try {
    user =
      localStorage.getItem('gogo_current_user') != null
        ? JSON.parse(localStorage.getItem('gogo_current_user'))
        : null;
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js  : getCurrentUser -> error', error);
    user = null;
  }
  return user;
};

export const setCurrentUser = (user) => {
  try {
    if (user) {
      localStorage.setItem('gogo_current_user', JSON.stringify(user));
      localStorage.setItem('userId', user.id);
    } else {
      localStorage.removeItem('gogo_current_user');
    }
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : setCurrentUser -> error', error);
  }
};

export const isSuperUserMenus = (isSuperUser, data) => {
  let newData = [];
  if (isSuperUser === 'false') {
    newData = data;
    adminMenus.forEach((menu) => {
      const index = newData.findIndex((item) => item.id === menu);
      newData.splice(index, 1);
    });
  } else {
    newData = data;
  }
  return newData;
};

export const menusByPermissions = (permissionsJSON, menuData) => {
  if (!permissionsJSON) {
    return [];
  }

  const userPermissions = JSON.parse(permissionsJSON)?.user_permissions || [];

  // Filtra el menú según los permisos del usuario
  const filterMenuByPermissions = (menuItems) => {
    return menuItems
      .filter((item) => {
        // Comprueba si el ID del menú está en los permisos del usuario
        return userPermissions.includes(item.id);
      })
      .map((item) => {
        // Si el menú tiene submenús, también filtra recursivamente los submenús
        if (item.subs) {
          return {
            ...item,
            subs: filterMenuByPermissions(item.subs),
          };
        }
        return item;
      });
  };

  return filterMenuByPermissions(menuData);
};

export const getAuthToken = () => {
  let token = null;
  try {
    if (localStorage.getItem('token')) {
      token = localStorage.getItem('token');
    }
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js  : getAuthToken -> error', error);
    token = null;
  }
  return token;
};

export const setAuthToken = (token, isSuperUser) => {
  try {
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('isSuperUser', isSuperUser);
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('isSuperUser');
    }
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : setAuthToken -> error', error);
  }
};

export const setEmailForgotPassword = (email) => {
  let res = null;
  try {
    const response = axios.post('/api/v1/forgot-password/', {
      email,
      location: window.location.origin,
    });
    res = response;
  } catch (error) {
    console.log(
      '>>>>: src/helpers/Utils.js : setEmailForgotPassword -> error',
      error
    );
    res = error;
  }
  return res;
};

export const setNewPassword = async (
  resetPasswordCode,
  newPassword,
  history
) => {
  try {
    const response = await axios.post('/api/v1/reset-password/', {
      token: resetPasswordCode,
      new_password: newPassword,
    });

    NotificationManager.success(
      'Your password has been updated successfully.',
      'Success',
      3000,
      null,
      null,
      ''
    );

    setTimeout(() => {
      history.push('/user/login');
    }, 1500);

    return response.data;
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : setNewPassword -> error', error);

    let errorMessage = 'An error occurred. Please try again.';

    if (error.response) {
      errorMessage = error.response.data.error || errorMessage;
    } else if (error.request) {
      errorMessage = 'Network error. Please check your connection.';
    }

    NotificationManager.warning(
      errorMessage,
      'Reset Password Error',
      3000,
      null,
      null,
      ''
    );

    return { error: errorMessage };
  }
};
